import { useState, useEffect } from "react";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Menu, Search, Bell, LogOut, User, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [cities, setCities] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [ctxCity, setCtxCity] = useState(() => localStorage.getItem("ctx_city") || "");
  const [ctxAgency, setCtxAgency] = useState(() => localStorage.getItem("ctx_agency") || "");

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem("ctx_city", ctxCity); }, [ctxCity]);
  useEffect(() => { localStorage.setItem("ctx_agency", ctxAgency); }, [ctxAgency]);

  const handleLogout = async () => { await logout(); nav("/login"); };

  const selectCls = "bg-white border border-[#E5E7EB] text-[#0A0A0A] text-xs rounded px-3 py-1.5 focus:border-[#0B1F3A] focus:outline-none";

  return (
    <header className="glass-header sticky top-0 z-30 h-16 flex items-center px-4 sm:px-6 gap-3">
      <button
        onClick={onMenuClick}
        className="lg:hidden text-[#6B7280] hover:text-[#0B1F3A]"
        data-testid="header-menu-btn"
      >
        <Menu size={22} />
      </button>

      {/* Context switchers */}
      <div className="hidden md:flex items-center gap-2">
        <select
          value={ctxCity}
          onChange={(e) => setCtxCity(e.target.value)}
          className={selectCls}
          data-testid="header-city-selector"
        >
          <option value="">Todas as cidades</option>
          {cities.map((c) => (
            <option key={c.id} value={c.id}>{c.name}/{c.state}</option>
          ))}
        </select>
        {user?.role === "admin_geral" && (
          <select
            value={ctxAgency}
            onChange={(e) => setCtxAgency(e.target.value)}
            className={selectCls}
            data-testid="header-agency-selector"
          >
            <option value="">Todas as imobiliárias</option>
            {agencies.map((a) => (
              <option key={a.id} value={a.id}>{a.trade_name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Global search */}
      <div className="flex-1 max-w-md ml-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            placeholder="Buscar solicitação, imóvel, imobiliária…"
            className="w-full bg-white border border-[#E5E7EB] text-[#0A0A0A] text-xs pl-9 pr-3 py-2 rounded focus:border-[#0B1F3A] focus:outline-none"
            data-testid="header-global-search"
          />
        </div>
      </div>

      <button className="text-[#6B7280] hover:text-[#0B1F3A] relative" data-testid="header-notifications-btn">
        <Bell size={20} />
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#FFD400] rounded-full ring-1 ring-white" />
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white border border-[#E5E7EB] rounded hover:border-[#0B1F3A]" data-testid="header-user-menu">
            <div className="w-7 h-7 rounded-full bg-[#0B1F3A] flex items-center justify-center text-xs font-bold text-[#FFD400]">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-xs font-semibold leading-tight text-[#0A0A0A]">{user?.name}</div>
              <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <ChevronDown size={14} className="text-[#9CA3AF]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-white border-[#E5E7EB] text-[#0A0A0A]">
          <DropdownMenuLabel>Minha conta</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[#E5E7EB]" />
          <DropdownMenuItem onClick={() => nav("/perfil")} data-testid="menu-profile">
            <User size={14} className="mr-2" /> Perfil
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
            <LogOut size={14} className="mr-2" /> Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
